import { useState, useRef, useEffect } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Camera, X, AlertTriangle, RefreshCw } from "lucide-react";
import { beep } from "@/lib/beep";

const FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.UPC_EAN_EXTENSION,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.CODABAR,
  Html5QrcodeSupportedFormats.QR_CODE,
];

function mapError(err) {
  const name = (err && (err.name || err.message || err.toString())) || "";
  if (/NotAllowed|Permission|Denied/i.test(name))
    return "Permissão de câmera negada. No iPhone: Ajustes › Safari (ou o app NEXO LOG) › Câmera › Permitir. No Android: toque no cadeado/permissões do site e habilite a Câmera. Depois toque em Tentar novamente.";
  if (/NotFound|Devices|Overconstrained/i.test(name))
    return "Nenhuma câmera disponível neste dispositivo.";
  if (/NotReadable|TrackStart|InUse/i.test(name))
    return "A câmera está em uso por outro aplicativo. Feche-o e tente novamente.";
  if (/secure|https/i.test(name))
    return "A câmera exige conexão segura (HTTPS).";
  return "Não foi possível abrir a câmera. Use a digitação manual do código.";
}

// Botão de câmera reutilizável para leitura de código de barras.
// Solicita permissão DENTRO do gesto do toque (essencial em iOS/PWA) e chama onDetected(codigo).
export default function CameraScannerButton({ onDetected, continuous = false, className = "h-14 w-14 shrink-0", variant = "outline", testid = "btn-open-camera" }) {
  const [camOpen, setCamOpen] = useState(false);
  const [canScan, setCanScan] = useState(false);
  const [camError, setCamError] = useState("");
  const [reads, setReads] = useState(0);
  const lastRef = useRef({ code: "", t: 0 });
  const scannerRef = useRef(null);
  const propsRef = useRef({ onDetected, continuous });
  propsRef.current = { onDetected, continuous };

  const openCamera = async () => {
    setCamError("");
    setCanScan(false);
    setReads(0);
    lastRef.current = { code: "", t: 0 };
    if (navigator.mediaDevices?.getUserMedia) {
      try {
        const probe = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } }, audio: false,
        });
        probe.getTracks().forEach((t) => t.stop());
      } catch (err) {
        setCamOpen(true);
        setCamError(mapError(err));
        return;
      }
    }
    setCanScan(true);
    setCamOpen(true);
  };

  useEffect(() => {
    if (!camOpen || !canScan) return;
    let cancelled = false;
    setCamError("");
    const config = {
      fps: 12,
      qrbox: (vw, vh) => { const m = Math.floor(Math.min(vw, vh) * 0.82); return { width: m, height: Math.floor(m * 0.6) }; },
      formatsToSupport: FORMATS,
      useBarCodeDetectorIfSupported: true,
      videoConstraints: { facingMode: { ideal: "environment" } },
    };

    const onDecoded = (decoded) => {
      if (cancelled) return;
      const cb = propsRef.current;
      if (cb.continuous) {
        const now = Date.now();
        if (decoded === lastRef.current.code && now - lastRef.current.t < 1200) return;
        lastRef.current = { code: decoded, t: now };
        try { beep(); } catch (_) {}
        try { navigator.vibrate && navigator.vibrate(60); } catch (_) {}
        setReads((n) => n + 1);
        cb.onDetected && cb.onDetected(decoded);
        return; // mantém a câmera aberta para leitura contínua
      }
      try { beep(); } catch (_) {}
      try { navigator.vibrate && navigator.vibrate(60); } catch (_) {}
      setCamOpen(false);
      cb.onDetected && cb.onDetected(decoded);
    };

    const boot = async (tries = 0) => {
      const el = document.getElementById("reader-cam");
      if (!el) {
        if (tries < 25 && !cancelled) return setTimeout(() => boot(tries + 1), 60);
        if (!cancelled) setCamError("Não foi possível iniciar o leitor. Use a digitação manual.");
        return;
      }
      const scanner = new Html5Qrcode("reader-cam", { verbose: false, formatsToSupport: FORMATS });
      scannerRef.current = scanner;
      try {
        await scanner.start({ facingMode: { ideal: "environment" } }, config, onDecoded, () => {});
      } catch (err) {
        try {
          const cams = await Html5Qrcode.getCameras();
          if (cams && cams.length) {
            const back = cams.find((c) => /back|rear|environment|traseira/i.test(c.label)) || cams[cams.length - 1];
            await scanner.start(back.id, config, onDecoded, () => {});
          } else { throw err; }
        } catch (err2) {
          if (!cancelled) setCamError(mapError(err2 && (err2.name || err2.message) ? err2 : err));
        }
      }
    };
    boot();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        try {
          const state = s.getState ? s.getState() : 2;
          if (state === 2) s.stop().then(() => s.clear()).catch(() => { try { s.clear(); } catch (_) {} });
          else { try { s.clear(); } catch (_) {} }
        } catch (_) {}
      }
    };
  }, [camOpen, canScan]);

  const close = () => { setCamOpen(false); setCanScan(false); };

  return (
    <>
      <Button type="button" variant={variant} className={className} onClick={openCamera} data-testid={testid}>
        <Camera className="w-6 h-6" />
      </Button>
      <Dialog open={camOpen} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">Leitor de Câmera</DialogTitle>
            <DialogDescription className="sr-only">Aponte a câmera para o código de barras para ler automaticamente.</DialogDescription>
          </DialogHeader>

          <div className="relative w-full rounded-md overflow-hidden border border-primary/40 bg-black">
            <div id="reader-cam" className="w-full min-h-[260px]" />
            {canScan && !camError && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center" data-testid="scan-guide">
                <div className="relative" style={{ width: "78%", height: "48%" }}>
                  <span className="absolute -top-0.5 -left-0.5 w-7 h-7 border-t-4 border-l-4 border-primary rounded-tl-md" />
                  <span className="absolute -top-0.5 -right-0.5 w-7 h-7 border-t-4 border-r-4 border-primary rounded-tr-md" />
                  <span className="absolute -bottom-0.5 -left-0.5 w-7 h-7 border-b-4 border-l-4 border-primary rounded-bl-md" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-7 h-7 border-b-4 border-r-4 border-primary rounded-br-md" />
                  <span className="scan-laser absolute left-2 right-2 h-0.5 bg-primary shadow-[0_0_12px_2px_rgba(245,158,11,0.8)]" />
                </div>
              </div>
            )}
          </div>

          {camError ? (
            <div className="space-y-2">
              <div className="flex items-start gap-2 rounded-md border border-red-600/50 bg-red-950/50 p-3 text-sm text-red-200" data-testid="cam-error">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{camError}</span>
              </div>
              <Button type="button" variant="secondary" className="w-full" onClick={openCamera} data-testid="btn-retry-camera">
                <RefreshCw className="w-4 h-4" /> Tentar novamente
              </Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center" data-testid="scan-help">
              {continuous
                ? <>Leitura contínua ativa — bipe vários produtos em sequência. <span className="text-primary font-bold" data-testid="scan-reads">Lidos: {reads}</span>. Toque em Concluir ao terminar.</>
                : <>Posicione o código de barras dentro da moldura (EAN-13, EAN-8, UPC, Code 128 e Master). A leitura é automática.</>}
            </p>
          )}
          <Button type="button" variant="outline" onClick={close} data-testid="btn-close-camera"><X className="w-4 h-4" /> {continuous ? "Concluir" : "Fechar"}</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
